'use client';
import React from 'react';
import styled from '@benzinga/themetron';
import { ArticleSidebar, AuthorInfo } from '@benzinga/article';
import { Author, injectNodeIdInCampaignBlocks } from '@benzinga/article-manager';
import Error from '../Error/Error';
import { ContentFeed, FeaturedCard, LoadMoreDevice } from '@benzinga/news';
import { StoryObject } from '@benzinga/basic-news-manager';
import { Layout } from '@benzinga/core-ui';
import { NodeLayout, WordpressSidebar } from '@benzinga/content-manager';
import { LayoutAdmin } from '@benzinga/blocks';

export interface AuthorTemplateProps {
  slug: string;
  author: Author;
  excludeContentTypes?: string;
  news: StoryObject[];
  layout: NodeLayout;
  showDefaultSidebar?: boolean;
  type?: string;
}

export const AuthorTemplate: React.FC<AuthorTemplateProps> = ({ author, excludeContentTypes, layout, news, type }) => {
  const [sidebar, setSidebar] = React.useState<WordpressSidebar | undefined | null>(layout?.sidebar);

  React.useEffect(() => {
    if (Array.isArray(sidebar?.blocks) && sidebar?.blocks?.length) {
      sidebar.blocks = injectNodeIdInCampaignBlocks(sidebar.blocks, 'article-sidebar', 204614 as unknown as string);
    }
    setSidebar(sidebar);
  }, [sidebar]);

  if (!author) {
    return <Error statusCode={404} />;
  }

  return (
    <AuthorLayoutWrapper className="author-layout-wrapper">
      <Layout
        className="author-container"
        layoutHeader={
          author ? (
            <>
              <LayoutAdmin pageUrl={author?.profileUrl} showCloudflareClearCache={true} />
              <AuthorInfo author={author} />
            </>
          ) : undefined
        }
        layoutMain={
          <div className="grid grid-cols-1 author-content-feed-wrapper">
            <h3 className="author-feed-heading">Articles by {author.name}</h3>
            <ContentFeed
              contentId="Author Page News"
              isInfinite
              limit={18}
              loadMore={true}
              loadMoreDevice={'all' as LoadMoreDevice}
              maxTimesLoaded={110}
              newsItemElement={(node, i) => (
                <FeaturedCard imageWidth={200} key={`item-${i}`} layout="feed" node={node} />
              )}
              nodes={news ? news : []}
              query={{
                allOriginalArticles: true,
                excludeContentTypes,
                limit: 18,
                type,
                userId: author.uid,
              }}
            />
          </div>
        }
        layoutMainClassName="w-full overflow-hidden"
        layoutSidebar={
          sidebar ? (
            <React.Suspense>
              <ArticleSidebar sidebar={sidebar} />
            </React.Suspense>
          ) : (
            false
          )
        }
      />
    </AuthorLayoutWrapper>
  );
};

const AuthorLayoutWrapper = styled.div`
  &.author-layout-wrapper {
    .author-container {
      margin: 0 auto;
      padding: 1rem;
    }
    .layout-header {
      max-width: 1400px;
      margin: 0 auto;
    }
    .author-info {
      border-bottom: 1px solid #dfdfdf;
      padding-bottom: 1rem;
    }
    .author-feed-heading {
      font-size: 20px;
      font-weight: 600;
      margin: 0 0 12px 0;
    }
    .author-content-feed-wrapper .post-card-wrapper {
      position: relative;
      border-bottom: 1px solid #f0f0f0;
      margin-bottom: 10px;
      padding: 10px 0 20px 0;
    }
    .author-content-feed-wrapper .post-card-feed .post-card-image {
      width: 115px;
      height: 115px;
      min-width: 115px;
      min-height: 115px;
    }
    .author-content-feed-wrapper .post-card-feed .post-card-text {
      all: unset;
      width: calc(100% - 115px);
      padding-left: 10px;
    }
    .author-content-feed-wrapper .post-card-feed .post-card-text .post-card-title {
      font-size: 20px;
    }
    .author-content-feed-wrapper .post-teaser {
      font-weight: 400;
      font-size: 14px;
    }
  }
`;
